/**
 * BlockingOverlay — overlay de tela cheia que BLOQUEIA a interação enquanto a IA
 * trabalha.
 *
 * Quando alguma mutation de IA está em andamento (ver useIsMutating({ mutationKey:
 * ['ai'] }) no AppLayout), este overlay cobre TODA a tela (Sidebar + Header +
 * conteúdo), captura cliques/scroll e exibe o LoadingSpinner + uma mensagem,
 * fazendo o usuário aguardar a IA terminar.
 *
 * Acessibilidade:
 *   - role="alertdialog" + aria-modal + aria-busy="true" + aria-live="polite"
 *   - Trava o scroll do body enquanto aberto (restaura ao fechar).
 *   - prefers-reduced-motion: herdado do LoadingSpinner (para de girar, permanece).
 *
 * Identidade: paleta C, estilos inline, pt-BR.
 */
import { useEffect } from 'react';
import { Cpu } from 'lucide-react';
import { C } from '@/theme/palette';

/**
 * @param {{ open: boolean, label?: string, sublabel?: string, progressLine?: string }} props
 */
export function BlockingOverlay({
  open,
  label = 'A IA está trabalhando…',
  sublabel = 'Pode levar alguns segundos. Aguarde a IA terminar.',
  progressLine = '',
}) {
  // Trava o scroll do body enquanto o overlay está aberto.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-busy="true"
      aria-live="polite"
      aria-label={label}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        // backdrop escuro semi-opaco (paleta C: black com alta opacidade)
        backgroundColor: 'rgba(10, 10, 10, 0.9)',
        backdropFilter: 'blur(3px)',
        // captura todos os cliques/interações por baixo
        pointerEvents: 'auto',
        cursor: 'wait',
      }}
    >
      {/* Anel pulsante (navaPulse) + ícone/texto piscando (navaLiveDot) */}
      <div style={{
        width: 60, height: 60, borderRadius: '50%', border: `2px solid ${C.cyber}`,
        display: 'grid', placeItems: 'center', animation: 'navaPulse 1.6s ease-out infinite',
      }}>
        <Cpu size={26} style={{ color: C.cyber, animation: 'navaLiveDot 1.6s ease-in-out infinite' }} />
      </div>
      <div style={{ textAlign: 'center', padding: '0 16px' }}>
        <p style={{ margin: 0, fontSize: 14.5, fontWeight: 600, color: C.neutral,
          animation: 'navaLiveDot 1.8s ease-in-out infinite' }}>
          {label}
        </p>
        {sublabel && (
          <p style={{ margin: '6px 0 0', fontSize: 12, color: C.neutralMuted, maxWidth: 380, lineHeight: 1.5 }}>
            {sublabel}
          </p>
        )}
        {/* ANDAMENTO ao vivo: última mensagem de fase publicada pelo backend
            (ex.: "Protótipo: fase 3 (~180 KB) — continuando o escopo…"), atualizada
            a cada 2s pelo feed de atividade. Mostra o AVANÇO real, não só a animação. */}
        {String(progressLine || '').trim() && (
          <div style={{
            margin: '14px auto 0', maxWidth: 440, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: 8, padding: '8px 14px', borderRadius: 999,
            border: `1px solid ${C.cyber}55`, background: `${C.cyber}12`,
          }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%', background: C.cyber, flex: '0 0 auto',
              animation: 'navaLiveDot 1.2s ease-in-out infinite',
            }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: C.cyber, lineHeight: 1.4 }}>
              {String(progressLine).slice(0, 160)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
